import { html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { Switch, SwitchEvent } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { TreeDropdown, TreeEvent } from "@omegagrid/tree";
import { loader as sheetjs } from "@omegagrid/plugin-xlsx";
import rp, { AppComponent } from "@reporting/rp-app-host";
import { AccountingRights } from "@reporting/rp-types/core";
import { ModelTreeAdapter } from "../adapters";
import {
	ACCEPTED_FILES, MAX_ATTACHMENT_BYTES, attachmentIcon, detectPastedFile, encodeText,
	isImageAttachment, isSpreadsheetFile, readTextFile, spreadsheetToText, textAttachment, textTypeOf,
} from "../attachments";
import { ChatAttachmentRef } from "../types";
import { style } from "./chatInput.style";

// below this width (px) of the input itself the switches collapse into a popover behind one button
const COMPACT_CONTROLS_WIDTH = 650;

export class ChatInputEvent extends Event {
	readonly text: string;
	readonly model: string;
	readonly includeData: boolean;
	readonly includeDefinition: boolean;
	readonly includeGroup: boolean;
	readonly enableThinking: boolean;
	readonly attachments: ChatAttachmentRef[];

	constructor(type: string, args?: Partial<ChatInputEvent>) {
		super(`chatinput.${type}`, {bubbles: true, composed: true});
		if (args) Object.assign(this, args);
	}
}

@customElement('rp-mf-ai-chatinput')
export class ChatInput extends AppComponent {

	static styles = [AppComponent.styles, style];

	private _text: string = '';
	@property({type: String})
	get text(): string { return this._text }
	set text(value: string) {
		if (this._text === value) return;
		this._text = value;
		this.requestUpdate();
	}

	@property({type: Boolean})
	disabledCommit = false;

	/** An answer is being generated and can be stopped: the Send button turns into Stop (`chatinput.cancel`). */
	@property({type: Boolean})
	cancellable = false;

	@property({type: String})
	model: string;

	// the common tools are always on (no Tools toggle); the switches below widen them
	@property({type: Boolean})
	includeData = false;

	// statement-defining tools; the toggle is offered only to users with the definition right
	@property({type: Boolean})
	includeDefinition = false;

	// reading the other companies of the accounting group; the toggle is offered only where there is one to read
	@property({type: Boolean})
	includeGroup = false;

	/** Whether the Group toggle is offered at all — served with the chat default (GET ai/default, canReadGroup). */
	@property({type: Boolean})
	canReadGroup = false;

	@property({type: Boolean})
	enableThinking = false;

	// at most one attachment per message (an image or a text file), kept as a list for the event/API shape
	@state()
	private _attachments: ChatAttachmentRef[] = [];

	// name of the spreadsheet being converted to text (a chip with a spinner; sending waits for it)
	@state()
	private _converting: string | null = null;

	// narrow input: the switches live in a popover (see COMPACT_CONTROLS_WIDTH)
	@state()
	private _compactControls = false;

	@state()
	private _togglesOpen = false;

	private _resizeObserver = new ResizeObserver(() => this._updateCompactControls());

	@query('.input')
	inputElement: HTMLTextAreaElement;

	@query('.btn-send')
	btnSendElement: HTMLDivElement;

	@query('.file-input')
	fileInput: HTMLInputElement;

	@query('.model-tree')
	modelTree: TreeDropdown;

	private _modelTreeAdapter: ModelTreeAdapter;
	get modelTreeAdapter() {
		return this._modelTreeAdapter ?? (this._modelTreeAdapter = new ModelTreeAdapter());
	}

	/** Whether the Definition toggle is offered at all: the definition right is required to define statements. */
	get canDefine(): boolean {
		return !!rp.userProvider?.hasRight(AccountingRights.Definition);
	}

	get canCommit() {
		return !this.disabledCommit && !this._converting && ((this.text ?? '').trim().length > 0 || this._attachments.length > 0);
	}

	/** How many of the switches are on - shown on the collapsed toggles button. */
	get activeToggleCount() {
		return [this.includeData, this.canDefine && this.includeDefinition, this.canReadGroup && this.includeGroup, this.enableThinking].filter(Boolean).length;
	}

	connectedCallback() {
		super.connectedCallback();
		this._resizeObserver.observe(this);
	}

	disconnectedCallback() {
		this._resizeObserver.disconnect();
		this._closeToggles();
		super.disconnectedCallback();
	}

	private _updateCompactControls() {
		if (this.offsetWidth === 0) return; // hidden (inactive tab) - keep the last known layout
		const compact = this.offsetWidth < COMPACT_CONTROLS_WIDTH;
		if (compact === this._compactControls) return;
		this._compactControls = compact;
		if (!compact) this._closeToggles();
	}

	// the popover closes on any mousedown outside of it (same rule as og-dropdown)
	private _onDocumentMouseDown = (e: MouseEvent) => {
		if (e.composedPath().some(elm => elm instanceof HTMLElement && (elm.classList.contains('toggles-popover') || elm.classList.contains('btn-toggles')))) return;
		this._closeToggles();
	};

	private _openToggles() {
		if (this._togglesOpen) return;
		this._togglesOpen = true;
		document.addEventListener('mousedown', this._onDocumentMouseDown);
	}

	private _closeToggles() {
		if (!this._togglesOpen) return;
		this._togglesOpen = false;
		document.removeEventListener('mousedown', this._onDocumentMouseDown);
	}

	private _toggleToggles() {
		if (this._togglesOpen) this._closeToggles();
		else this._openToggles();
	}

	_onInput = () => {
		this._text = this.inputElement.value;
		this.btnSendElement?.classList.toggle('disabled', !this.canCommit);
	};

	commit() {
		if (!this.canCommit) return;
		const selectedItems = [...this.modelTree.selectedItems.keys()];
		this.dispatchEvent(new ChatInputEvent('commit', {
			text: this._text,
			model: selectedItems.length > 0 ? selectedItems[0] : null,
			includeData: this.includeData,
			includeDefinition: this.canDefine && this.includeDefinition,
			includeGroup: this.canReadGroup && this.includeGroup,
			enableThinking: this.enableThinking,
			attachments: this._attachments,
		}));
		this._text = '';
		this.inputElement.value = '';
		this._attachments = [];
		this.btnSendElement?.classList.toggle('disabled', !this.canCommit);
	}

	// Let the chat remember its state (toggles + model) for the next time, per user and accounting.
	private _emitSettings() {
		this.dispatchEvent(new ChatInputEvent('settings', {
			model: this.model,
			includeData: this.includeData,
			includeDefinition: this.canDefine && this.includeDefinition,
			includeGroup: this.canReadGroup && this.includeGroup,
			enableThinking: this.enableThinking,
		}));
	}

	private _onModelChange = (e: TreeEvent) => {
		if (!e.node?.key || e.node.key === this.model) return;
		this.model = e.node.key;
		this._emitSettings();
	};

	// Turns the first given file into the message's attachment, replacing the current one: an image
	// as base64 (data-URL prefix stripped), a text file (CSV/TXT/JSON) decoded and re-encoded as UTF-8,
	// a spreadsheet converted to text.
	private async _addFiles(files: Iterable<File>) {
		const file = [...files][0];
		if (!file) return;
		if (file.size > MAX_ATTACHMENT_BYTES) {
			rp.host.showAlert({ content: msg('ai.attachtoolarge'), type: 'warning' });
			return;
		}
		const textType = textTypeOf(file);
		if (textType) {
			const bytes = encodeText(await readTextFile(file));
			if (bytes.length > MAX_ATTACHMENT_BYTES) { // UTF-16 or Windows-1250 input can grow when re-encoded
				rp.host.showAlert({ content: msg('ai.attachtoolarge'), type: 'warning' });
				return;
			}
			this._setAttachment(textAttachment(bytes, file.name, textType));
		} else if (isSpreadsheetFile(file)) {
			await this._addSpreadsheet(file);
		} else if (file.type.startsWith('image/')) {
			this._setAttachment(await this._fileToAttachment(file));
		} else {
			rp.host.showAlert({ content: msg('ai.attachunsupported'), type: 'warning' });
		}
	}

	// A spreadsheet (XLSX/XLS/ODS) becomes a CSV/text attachment: no provider reads workbooks, so it is
	// converted here with SheetJS, which the OmegaGrid xlsx plugin loads on demand (first use downloads it).
	private async _addSpreadsheet(file: File) {
		this._converting = file.name;
		this.btnSendElement?.classList.toggle('disabled', true);
		try {
			const [XLSX, buffer] = await Promise.all([sheetjs.get(), file.arrayBuffer()]);
			const converted = spreadsheetToText(new Uint8Array(buffer), XLSX, file.name);
			if (!converted) {
				rp.host.showAlert({ content: msg('ai.attachempty'), type: 'warning' });
				return;
			}
			const bytes = encodeText(converted.text);
			if (bytes.length > MAX_ATTACHMENT_BYTES) { // a compressed workbook can expand well beyond its file size
				rp.host.showAlert({ content: msg('ai.attachtoolarge'), type: 'warning' });
				return;
			}
			this._setAttachment(textAttachment(bytes, converted.fileName, converted.mediaType));
		} catch (e) {
			console.error('Spreadsheet conversion failed', e);
			rp.host.showAlert({ content: msg('ai.attachconvertfailed'), type: 'danger' });
		} finally {
			this._converting = null;
			this.btnSendElement?.classList.toggle('disabled', !this.canCommit);
		}
	}

	private _setAttachment(attachment: ChatAttachmentRef | null) {
		this._attachments = attachment ? [attachment] : [];
		this.btnSendElement?.classList.toggle('disabled', !this.canCommit);
	}

	private _fileToAttachment(file: File): Promise<ChatAttachmentRef> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				const result = String(reader.result ?? '');
				const data = result.includes(',') ? result.slice(result.indexOf(',') + 1) : result;
				resolve({ file_name: file.name, media_type: file.type, data });
			};
			reader.onerror = () => reject(reader.error);
			reader.readAsDataURL(file);
		});
	}

	private _onFileInputChange = (e: Event) => {
		const input = e.target as HTMLInputElement;
		if (input.files?.length) this._addFiles(input.files);
		input.value = '';
	};

	// Ctrl+V: a screenshot becomes the image attachment; text that looks like file content (a long
	// JSON, a table, a long text — see detectPastedFile) becomes a text attachment instead of
	// flooding the message. Neither is inserted into the textarea.
	private _onPaste = (e: ClipboardEvent) => {
		const files = [...(e.clipboardData?.items ?? [])]
			.filter(item => item.kind === 'file' && item.type.startsWith('image/'))
			.map(item => item.getAsFile())
			.filter((f): f is File => !!f);
		if (files.length) {
			e.preventDefault();
			this._addFiles(files);
			return;
		}
		if (this._attachments.length) return; // the single slot is taken: paste as text
		const text = e.clipboardData?.getData('text/plain') ?? '';
		const kind = detectPastedFile(text);
		if (!kind) return;
		e.preventDefault();
		const bytes = encodeText(text);
		if (bytes.length > MAX_ATTACHMENT_BYTES) {
			rp.host.showAlert({ content: msg('ai.attachtoolarge'), type: 'warning' });
			return;
		}
		this._setAttachment(textAttachment(bytes, `pasted.${kind.extension}`, kind.mediaType));
	};

	_onKeyDown = (e: KeyboardEvent) => {
		if (e.key === 'Enter' && !e.ctrlKey && !e.shiftKey) {
			e.preventDefault();
			this.commit();
		}
	};

	_onSwitchChange = async (e: SwitchEvent) => {
		const switchElm = e.target as Switch;
		const state = switchElm.state === 'on';
		switch (switchElm.id) {
			case 'toggle-data':
				this.includeData = state;
				break;
			case 'toggle-definition':
				this.includeDefinition = state;
				break;
			case 'toggle-group':
				this.includeGroup = state;
				break;
			case 'toggle-thinking':
				this.enableThinking = state;
				break;
		}
		this._emitSettings();
	}

	// The switches are rendered exactly once (inline or in the popover); _onSwitchChange tells them apart by their id.
	private _renderToggles() {
		return html`
			<label class="context-toggle" title="${msg('ai.datatoggle')}">
				<og-switch
					id="toggle-data"
					shape="round"
					.state="${this.includeData ? 'on' : 'off'}"
					@change="${this._onSwitchChange}">
				</og-switch>
				<span>${msg('Data')}</span>
			</label>

			${this.canDefine ? html`
				<label class="context-toggle" title="${msg('ai.definitiontoggle')}">
					<og-switch
						id="toggle-definition"
						shape="round"
						.state="${this.includeDefinition ? 'on' : 'off'}"
						@change="${this._onSwitchChange}">
					</og-switch>
					<span>${msg('Definition')}</span>
				</label>
			` : ''}

			${this.canReadGroup ? html`
				<label class="context-toggle" title="${msg('ai.grouptoggle')}">
					<og-switch
						id="toggle-group"
						shape="round"
						.state="${this.includeGroup ? 'on' : 'off'}"
						@change="${this._onSwitchChange}">
					</og-switch>
					<span>${msg('Group')}</span>
				</label>
			` : ''}

			<label class="context-toggle" title="${msg('ai.thinkingtoggle')}">
				<og-switch
					id="toggle-thinking"
					shape="round"
					.state="${this.enableThinking ? 'on' : 'off'}"
					@change="${this._onSwitchChange}">
				</og-switch>
				<span>${msg('Thinking')}</span>
			</label>
		`;
	}

	private _renderCompactToggles() {
		const count = this.activeToggleCount;
		return html`
			<div
				class="btn-toggles ${this._togglesOpen ? 'open' : ''}"
				title="${msg('Settings')}"
				@click="${() => this._toggleToggles()}">
				<og-icon icon="sliders"></og-icon>
				${count > 0 ? html`<span class="badge">${count}</span>` : ''}
			</div>

			${this._togglesOpen ? html`
				<div class="toggles-popover">${this._renderToggles()}</div>
			` : ''}
		`;
	}

	render = () => html`
		<textarea
			class="input"
			spellcheck="false"
			.placeholder="${msg('ai.inputplaceholder')}"
			@input="${this._onInput}"
			@paste="${this._onPaste}"
			@keydown="${this._onKeyDown}">${this.text}</textarea>

		${this._attachments.length || this._converting ? html`
			<div class="attachments">
				${this._converting ? html`
					<div class="attachment attachment-file attachment-converting" title="${this._converting}">
						<og-loader></og-loader>
						<span class="attachment-name">${this._converting}</span>
						<span class="attachment-status">${msg('ai.attachconverting')}</span>
					</div>
				` : ''}
				${this._attachments.map(att => isImageAttachment(att) ? html`
					<div class="attachment" title="${att.file_name ?? ''}">
						<img src="data:${att.media_type};base64,${att.data}" alt="${att.file_name ?? ''}" />
						<div class="attachment-remove" @click="${() => this._setAttachment(null)}">
							<og-icon icon="times"></og-icon>
						</div>
					</div>
				` : html`
					<div class="attachment attachment-file" title="${att.file_name ?? ''}">
						<og-icon icon="${attachmentIcon(att)}"></og-icon>
						<span class="attachment-name">${att.file_name ?? ''}</span>
						<div class="attachment-remove" @click="${() => this._setAttachment(null)}">
							<og-icon icon="times"></og-icon>
						</div>
					</div>
				`)}
			</div>
		` : ''}

		<input
			type="file"
			class="file-input"
			accept="${ACCEPTED_FILES}"
			hidden
			@change="${this._onFileInputChange}" />

		<div class="controls">
			<div style="flex: 0 0 100px">
				<og-tree-dropdown
					dynamicWidth
					color="transparent"
					openDirection="up"
					class="model-tree"
					.adapter="${this.modelTreeAdapter}"
					.value="${this.model ? [[this.model]] : null}"
					@change="${this._onModelChange}">
				</og-tree-dropdown>
			</div>

			${this._compactControls ? this._renderCompactToggles() : this._renderToggles()}

			<div style="flex: 1"></div>

			<div
				class="btn-attach"
				title="${msg('ai.attachfile')}"
				@click="${() => this.fileInput?.click()}">
				<og-icon icon="paperclip"></og-icon>
			</div>

			<div style="flex: 0">
				${this.cancellable ? html`
					<div
						class="btn-stop"
						title="${msg('Abort')}"
						@click="${() => this.dispatchEvent(new ChatInputEvent('cancel'))}">
						<og-icon icon="stop"></og-icon>
					</div>
				` : html`
					<div
						class="btn-send ${this.canCommit ? '' : 'disabled'}"
						@click="${() => this.commit()}">
						<og-icon icon="send"></og-icon>
					</div>
				`}
			</div>
		</div>
	`;

}
